/** Phase 6 command shapes: the vendor directory, its categories and the
 * per-venue preference (SPEC §3.11, §6.1 item 13a, §10.5).
 *
 * The booking side of vendors already has its shapes in `automation.ts`, where
 * Phase 5 put them. What is new here is the company-wide directory a booking
 * vendor can now be taken from, and the per-venue preference that decides what
 * the picker offers first.
 *
 * Primitives come from `common.ts` so a date, an id or a percentage means the
 * same thing here as everywhere else.
 */
import { z } from "zod";
import { dateStr, id } from "./common";

// ---------------------------------------------------------------- categories

/** A key is how a seeded category survives being renamed: the company calls it
 * what it likes, and a re-seed still recognises its own row. Nothing branches on
 * a key except `other`, which is where an import puts what it cannot place. */
export const vendorCategoryKey = z.string().trim().toLowerCase().regex(/^[a-z0-9_]{1,40}$/, "Letters, numbers and underscores only");

export const vendorCategorySave = z.object({
  key: vendorCategoryKey.optional(),
  name: z.string().trim().min(1).max(80),
  sort: z.number().int().min(0).max(9999).optional(),
  active: z.union([z.boolean(), z.literal(0), z.literal(1)]).optional(),
});
export type VendorCategorySave = z.infer<typeof vendorCategorySave>;

export const vendorCategoryPatch = vendorCategorySave.partial().strict()
  .refine(value => Object.keys(value).length > 0, "Choose a category detail to change.");
export type VendorCategoryPatch = z.infer<typeof vendorCategoryPatch>;

// ---------------------------------------------------------------- the directory

/** SPEC §3.11: `none` until a certificate is on file, `expired` once its own
 * expiry date has passed. It is derived from `coiExpiresOn`, never set by hand,
 * so a certificate cannot claim to be valid after the day it ran out. */
export const vendorCoiStatus = z.enum(["none", "on_file", "expired"]);
export const vendorSource = z.enum(["manual", "booking"]);

const trimmed = (max: number) => z.string().trim().max(max).nullable().optional();

export const vendorSave = z.object({
  name: z.string().trim().min(1).max(160),
  categoryId: id.nullable().optional(),
  contactName: trimmed(160),
  phone: trimmed(40),
  email: trimmed(200),
  website: trimmed(300),
  instagram: trimmed(120),
  address: trimmed(300),
  notes: trimmed(2000),
  licenseNo: trimmed(80),
  /** SPEC §3.11 says 1–5 and optional. A vendor nobody has rated is null, which
   * is not the same as a vendor rated zero. */
  rating: z.number().int().min(1).max(5).nullable().optional(),
  coiExpiresOn: dateStr.nullable().optional(),
  active: z.union([z.boolean(), z.literal(0), z.literal(1)]).optional(),
});
export type VendorSave = z.infer<typeof vendorSave>;

export const vendorPatch = vendorSave.partial().strict()
  .refine(value => Object.keys(value).length > 0, "Choose a vendor detail to change.");
export type VendorPatch = z.infer<typeof vendorPatch>;

/** What a rule needs to judge a directory entry, independent of how it was
 * loaded. Kept separate from `vendorSave` because a stored row has an id, a
 * derived status and an expiry the form never sends. */
export const vendorRecord = z.object({
  name: z.string().min(1).max(160),
  email: z.string().max(200).nullable(),
  phone: z.string().max(40).nullable(),
  website: z.string().max(300).nullable(),
  coiStatus: vendorCoiStatus,
  coiExpiresOn: z.string().nullable(),
  rating: z.number().int().min(1).max(5).nullable(),
});

// ---------------------------------------------------------------- preference

export const vendorVenuePrefSave = z.object({
  venueId: id,
  preferred: z.union([z.boolean(), z.literal(0), z.literal(1)]),
  notes: z.string().trim().max(500).nullable().optional(),
});
export type VendorVenuePrefSave = z.infer<typeof vendorVenuePrefSave>;

// ---------------------------------------------------------------- queries

/** The picker and the list share one query shape, so "what the list showed" and
 * "what the picker offered" cannot drift apart. */
export const vendorQuery = z.object({
  q: z.string().trim().max(120).optional(),
  categoryId: id.optional(),
  venueId: id.optional(),
  /** Only vendors preferred at `venueId`. Meaningless without one. */
  preferredOnly: z.enum(["0", "1"]).optional(),
  includeInactive: z.enum(["0", "1"]).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
}).refine(value => value.preferredOnly !== "1" || Boolean(value.venueId), {
  message: "Preferred is per venue, so it needs a venue.",
  path: ["preferredOnly"],
});
export type VendorQuery = z.infer<typeof vendorQuery>;

/** Attaching from the directory: the id, plus only what is specific to this
 * booking. Name and contact details are snapshotted from the directory by the
 * service, not sent by the caller, so a client cannot attach one vendor under
 * another's name. */
export const bookingVendorAttach = z.object({
  vendorId: id,
  role: z.string().trim().max(160).nullable().optional(),
  arrivalAt: z.string().trim().max(40).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export type BookingVendorAttach = z.infer<typeof bookingVendorAttach>;

export const bookingVendorReorder = z.object({ ids: z.array(id).min(1).max(100) });

export const VENDOR_COI_MAX_BYTES = 10 * 1024 * 1024;
export const vendorCoiMetadata = z.object({
  name: z.string().min(1).max(255),
  size: z.number().int().min(1).max(VENDOR_COI_MAX_BYTES),
  expiresOn: dateStr.nullable().optional(),
});
